import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Building2, Plus, Search, MapPin, Store, Box, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MallsPage() {
  const { api, isSuper, isGroupAdmin } = useAuth();
  const navigate = useNavigate();
  const [malls, setMalls] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    group_id: '',
    city: '',
    state: '',
    country: 'India',
    address: '',
    total_area_sqft: 0,
    floors: 1,
    parking_capacity: 0,
    opening_year: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mallsRes, groupsRes] = await Promise.all([
        api.get('/malls'),
        api.get('/groups'),
      ]);
      setMalls(mallsRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load malls');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.post('/malls', {
        ...formData,
        total_area_sqft: parseFloat(formData.total_area_sqft) || 0,
        floors: parseInt(formData.floors) || 1,
        parking_capacity: parseInt(formData.parking_capacity) || 0,
        opening_year: formData.opening_year ? parseInt(formData.opening_year) : null,
      });
      toast.success('Mall created successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create mall');
    } finally {
      setSaving(false);
    }
  };

  const openNewDialog = () => {
    setFormData({
      name: '',
      group_id: groups[0]?.id || '',
      city: '',
      state: '',
      country: 'India',
      address: '',
      total_area_sqft: 0,
      floors: 1,
      parking_capacity: 0,
      opening_year: null,
    });
    setDialogOpen(true);
  };

  const filteredMalls = malls.filter((mall) => {
    const matchesSearch =
      mall.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mall.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGroup = filterGroup === 'all' || mall.group_id === filterGroup;
    return matchesSearch && matchesGroup;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="malls-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Malls</h2>
          <p className="text-muted-foreground">{malls.length} malls in your portfolio</p>
        </div>
        {(isSuper || isGroupAdmin) && (
          <Button className="btn-primary" onClick={openNewDialog} data-testid="add-mall-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Mall
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="search-input-wrapper flex-1 max-w-md">
          <Search className="search-icon w-4 h-4" />
          <Input
            placeholder="Search malls by name or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-dark pl-10"
            data-testid="search-malls"
          />
        </div>
        <Select value={filterGroup} onValueChange={setFilterGroup}>
          <SelectTrigger className="w-[200px] input-dark" data-testid="filter-group">
            <SelectValue placeholder="Filter by group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            {groups.map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Malls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMalls.map((mall, index) => (
          <Card
            key={mall.id}
            className="glass-card hover:border-primary/30 transition-all duration-300 cursor-pointer animate-fade-in group"
            style={{ animationDelay: `${index * 0.05}s` }}
            onClick={() => navigate(`/malls/${mall.id}`)}
            data-testid={`mall-card-${mall.id}`}
          >
            <CardContent className="p-0">
              {/* Mall Image */}
              <div className="relative h-40 overflow-hidden rounded-t-xl">
                <img
                  src={mall.image_url || 'https://images.unsplash.com/photo-1766600115358-1c5ce9c7c87d?w=800'}
                  alt={mall.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="font-semibold text-white">{mall.name}</h3>
                  <div className="flex items-center gap-1 text-xs text-white/80 mt-1">
                    <MapPin className="w-3 h-3" />
                    {mall.city}, {mall.state}
                  </div>
                </div>
                <Badge className="absolute top-3 right-3 bg-black/50 text-white border-0">
                  {mall.group_name}
                </Badge>
              </div>

              {/* Mall Info */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-lg font-bold font-mono">{mall.floors}</p>
                    <p className="text-xs text-muted-foreground">Floors</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold font-mono">{mall.shop_count}</p>
                    <p className="text-xs text-muted-foreground">Shops</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold font-mono">{(mall.total_area_sqft / 1000).toFixed(0)}K</p>
                    <p className="text-xs text-muted-foreground">Sq ft</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Occupancy</span>
                    <span className="font-mono font-medium">{mall.occupancy_rate?.toFixed(1)}%</span>
                  </div>
                  <Progress value={mall.occupancy_rate || 0} className="h-2" />
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t border-border/40">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/mall/${mall.id}/manage`);
                    }}
                    data-testid={`manage-btn-${mall.id}`}
                  >
                    Manage
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/digital-twin/${mall.id}`);
                    }}
                    data-testid={`twin-btn-${mall.id}`}
                  >
                    <Box className="w-3 h-3 mr-1" />
                    3D
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredMalls.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No malls found</p>
        </div>
      )}

      {/* Add Mall Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-panel sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Mall</DialogTitle>
            <DialogDescription>Create a new mall in your portfolio</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Mall Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-dark"
                    required
                    data-testid="mall-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group">Group *</Label>
                  <Select
                    value={formData.group_id}
                    onValueChange={(value) => setFormData({ ...formData, group_id: value })}
                  >
                    <SelectTrigger className="input-dark" data-testid="mall-group-select">
                      <SelectValue placeholder="Select group" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="input-dark"
                    required
                    data-testid="mall-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="input-dark"
                    required
                    data-testid="mall-state-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-dark"
                  required
                  data-testid="mall-address-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="area">Area (sq ft)</Label>
                  <Input
                    id="area"
                    type="number"
                    value={formData.total_area_sqft}
                    onChange={(e) => setFormData({ ...formData, total_area_sqft: e.target.value })}
                    className="input-dark"
                    data-testid="mall-area-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="floors">Floors</Label>
                  <Input
                    id="floors"
                    type="number"
                    value={formData.floors}
                    onChange={(e) => setFormData({ ...formData, floors: e.target.value })}
                    className="input-dark"
                    min={1}
                    data-testid="mall-floors-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parking">Parking</Label>
                  <Input
                    id="parking"
                    type="number"
                    value={formData.parking_capacity}
                    onChange={(e) => setFormData({ ...formData, parking_capacity: e.target.value })}
                    className="input-dark"
                    data-testid="mall-parking-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-mall-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Mall'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
